import { SectionTreeItem } from './SectionTreeItem';
import { getItemPrefix } from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/case-tree/common';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useLocation, useNavigate } from 'react-router-dom';

import styles from './CalendarConfigurationContentTree.module.scss';

const sections: (
  | 'general'
  | 'calendar_grid'
  | 'filter_settings'
  | 'patients'
  | 'mini_calendar'
)[] = [
  'general',
  'calendar_grid',
  'filter_settings',
  'patients',
  'mini_calendar',
];

export const CalendarConfigurationContentTree: React.FC = () => {
  const i18n = useScopedTranslation('calendar_configuration');
  const navigate = useNavigate();

  const location = useLocation();

  const changeCurrentItem = useCallback(
    (itemId: string) => {
      const elementId = `section_${itemId}`;
      document.getElementById(elementId)?.scrollIntoView();
      navigate({ hash: elementId, search: location.search }, { replace: true });
    },
    [history],
  );

  const lastItemPrefix = useMemo(() => {
    if (sections && sections.length > 0) {
      return getItemPrefix(sections[sections.length - 1]);
    }
    return undefined;
  }, [sections]);

  useEffect(
    function setupIntersectionObserverToHighlightActiveCaseItem() {
      // stores information if the block is visible currently. Index is position of the block (from the top)
      const itemVisibilities: { id: string; visible: boolean }[] = [];
      // stores the position of a block. Key is block key, value is position of a block
      const itemIndexes: Record<string, number> = {};
      let index = 0;

      function addItemToIndexesAndVisibilities(id: string) {
        itemIndexes[id] = index;
        itemVisibilities[index] = { id: id, visible: false };
        index++;
      }

      sections.forEach((name) => {
        addItemToIndexesAndVisibilities(name);
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute('data-section-item-id');
          if (!id) {
            return;
          }
          const isVisible = entry.intersectionRatio > 0;

          const item = itemVisibilities[itemIndexes[id]];
          if (!item) {
            console.error(
              'item is undefined!',
              itemVisibilities,
              itemIndexes,
              id,
            );
            return;
          }
          item.visible = isVisible;

          let firstIndex = itemVisibilities.findIndex((x) => x.visible);
          if (firstIndex < 0) {
            firstIndex = itemVisibilities.length - 1;
          }

          const firstVisibleElementId = itemVisibilities[firstIndex].id; // selected element
          // all previous elements that need to be highlighted
          const highlightedData = itemVisibilities
            .map((x) => x.id)
            .slice(0, firstIndex);

          document!
            .querySelectorAll(`[data-section-item-link]`)
            .forEach((x) => {
              x.removeAttribute('data-selected');
              x.removeAttribute('data-highlighted-line');
            });

          // add data-selected attribute to a first selected node
          const selectedContentsTreeItem = document!.querySelector(
            `[data-section-item-link="${firstVisibleElementId}"]`,
          );

          // highlight vertical line of all previous elements
          highlightedData.forEach((id) =>
            document!
              .querySelector(`[data-section-item-link="${id}"]`)
              ?.setAttribute('data-highlighted-line', 'true'),
          );

          selectedContentsTreeItem?.setAttribute('data-selected', 'true');
          selectedContentsTreeItem?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
          navigate(
            {
              search: location.search,
              hash: `#section_${firstVisibleElementId}`,
            },
            { replace: true },
          );
        });
      });

      // Track all sections that have an `id` applied
      document.querySelectorAll('[data-section-item-id]').forEach((section) => {
        observer.observe(section);
      });

      return () => {
        observer.disconnect();
      };
    },
    [sections],
  );

  return (
    <div className={styles.contentContainer}>
      <span className={styles.title}>
        {i18n.t('contents.title').toUpperCase()}
      </span>
      <div className={styles.mainContainer}>
        <div className={styles.treeContainer}>
          {sections.map((name) => (
            <div key={name}>
              <SectionTreeItem
                id={name}
                title={(i18n.t('blocks') as any)[name]}
                onClick={changeCurrentItem}
                isLastElement={name === lastItemPrefix}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
